import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import './styles/ConnectSelect.css';

/**
 * A select whose open list we can actually style.
 *
 * A native <select> draws its list with the operating system, so the panel
 * could never match the rest of the app. This replaces it with the
 * select-only combobox pattern, which means the keyboard behaviour people
 * expect has to be rebuilt by hand rather than inherited:
 *
 *   closed  Enter / Space / Arrow / Home / End   open the list
 *   open    Arrow, Home, End                     move the highlight
 *           Enter / Space                        choose and close
 *           Escape, Tab, click outside           close, keeping the value
 *           typing                               jump to a matching option
 *
 * Focus stays on the button throughout and aria-activedescendant points at
 * the highlighted option, so a screen reader announces the list, the
 * position in it and the current selection.
 *
 * On a narrow screen the list opens as a bottom sheet rather than a dropdown
 * pinned under the button, with rows big enough to hit with a thumb.
 */

export default function ConnectSelect({
  value,
  onChange,
  options,
  placeholder = 'Choose…',
  ariaLabel,
  labelledBy,
  variant = 'field',
  disabled = false,
  id,
}) {
  const reactId = useId();
  const baseId = id || `cs-${reactId.replace(/:/g, '')}`;
  const listId = `${baseId}-list`;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const typeahead = useRef({ buffer: '', timer: null });

  const selectedIndex = options.findIndex((o) => String(o.value) === String(value));
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;

  const close = useCallback((refocus = true) => {
    setOpen(false);
    setActiveIndex(-1);
    if (refocus && buttonRef.current) buttonRef.current.focus();
  }, []);

  const openList = useCallback((startAt) => {
    if (disabled) return;
    setOpen(true);
    setActiveIndex(
      typeof startAt === 'number'
        ? startAt
        : (selectedIndex >= 0 ? selectedIndex : 0)
    );
  }, [disabled, selectedIndex]);

  const choose = useCallback((index) => {
    const option = options[index];
    if (!option) return;
    onChange(option.value);
    close();
  }, [options, onChange, close]);

  // Clicking away commits whatever was already selected.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target) &&
        listRef.current && !listRef.current.contains(e.target)
      ) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Keep the highlighted option in view when arrowing through a long list.
  useEffect(() => {
    if (!open || activeIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-index="${activeIndex}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  const jumpToTyped = useCallback((char) => {
    const t = typeahead.current;
    if (t.timer) clearTimeout(t.timer);
    t.buffer += char.toLowerCase();
    t.timer = setTimeout(() => { t.buffer = ''; }, 600);

    const from = open ? activeIndex : selectedIndex;
    const ordered = options
      .map((o, i) => ({ o, i }))
      .slice(from + 1)
      .concat(options.map((o, i) => ({ o, i })).slice(0, from + 1));

    const hit = ordered.find(({ o }) => o.label.toLowerCase().startsWith(t.buffer));
    if (!hit) return;

    if (open) setActiveIndex(hit.i);
    else onChange(hit.o.value);
  }, [open, activeIndex, selectedIndex, options, onChange]);

  const onKeyDown = (e) => {
    if (disabled) return;

    if (!open) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
        e.preventDefault();
        openList();
        return;
      }
      if (e.key === 'Home') { e.preventDefault(); openList(0); return; }
      if (e.key === 'End') { e.preventDefault(); openList(options.length - 1); return; }
    } else {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, options.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Home') { e.preventDefault(); setActiveIndex(0); return; }
      if (e.key === 'End') { e.preventDefault(); setActiveIndex(options.length - 1); return; }
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(activeIndex); return; }
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key === 'Tab') { close(false); return; }
    }

    if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      jumpToTyped(e.key);
    }
  };

  return (
    <div className={`cs cs-${variant} ${open ? 'is-open' : ''}`}>
      <button
        ref={buttonRef}
        type="button"
        id={baseId}
        className="cs-button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listId}
        aria-label={ariaLabel}
        aria-labelledby={labelledBy}
        aria-activedescendant={open && activeIndex >= 0 ? `${baseId}-opt-${activeIndex}` : undefined}
        disabled={disabled}
        onClick={() => (open ? close() : openList())}
        onKeyDown={onKeyDown}
      >
        <span className={`cs-value ${selected ? '' : 'is-placeholder'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <svg className="cs-chevron" width="12" height="8" viewBox="0 0 12 8" aria-hidden="true">
          <path d="M1 1.5L6 6.5L11 1.5" fill="none" stroke="currentColor"
                strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && <div className="cs-backdrop" aria-hidden="true" />}

      {open && (
        <ul ref={listRef} id={listId} className="cs-list" role="listbox"
            aria-label={ariaLabel} tabIndex={-1}>
          {options.map((o, i) => {
            const isSelected = String(o.value) === String(value);
            return (
              <li
                key={o.value}
                id={`${baseId}-opt-${i}`}
                data-index={i}
                role="option"
                aria-selected={isSelected}
                className={`cs-option ${i === activeIndex ? 'is-active' : ''} ${isSelected ? 'is-selected' : ''}`}
                onMouseEnter={() => setActiveIndex(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(i)}
              >
                <span className="cs-option-label">{o.label}</span>
                {isSelected && (
                  <svg width="12" height="10" viewBox="0 0 12 10" aria-hidden="true" className="cs-tick">
                    <path d="M1 5L4.5 8.5L11 1.5" fill="none" stroke="currentColor"
                          strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
